import { useParams } from 'react-router-dom'
import { useState } from 'react'
import SuspendModal from '../../components/admin/SuspendModal'
import USER_TYPE from '../../constants/userType'
import DocumentListing from '../../components/common/DocumentListing'
import { useShopDetails } from '../../hooks/useShopDetails'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ShopActionButtons from '../../components/admin/Shops/ShopActionButtons'
import DisplayActiveSuspended from '../../components/admin/DisplayActiveSuspended'
import { formatTime } from '../../utils/date.util'
import InfoGrid from '../../components/common/InfoGrid'
import UserTypeDisplay from '../../components/common/UserTypeDisplay'
import SubscriptionDetails from '../../components/common/SubscriptionDetails'
import DisplayShopName from '../../components/common/DisplayShopName'

export default function WholesellerDetailsPage() {
  const { shopSlug } = useParams()
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false)

  const { shop, documents, isLoading } = useShopDetails(USER_TYPE.wholeseller, shopSlug)

  if (isLoading) return <LoadingSpinner />

  const onCloseSuspendModal = () => setIsSuspendModalOpen(false)

  const {
    user,
    shopName,
    buildingName,
    floor,
    shopNumber,
    isApproved,
    approvedAt,
    approvedBy,
    userSubscription,
    violationReports,
    activeTickets,
  } = shop

  const hasDocuments = Array.isArray(shop?.shopDocuments) && shop.shopDocuments.length > 0

  const renderStatus = () => {
    return (
      <>
        <DisplayActiveSuspended isActive={user?.isActive} suspendedBy={user?.suspendedBy} />
        {!user?.isActive && user?.suspendedAt && (
          <span className="text-foreground-secondary text-sm block mt-1">
            {`Suspended at: ${formatTime(user?.suspendedAt)}`} by {user?.suspender?.name}
          </span>
        )}

        {!user?.isActive && user?.suspensionReason && (
          <span className="text-foreground-secondary text-sm block mt-1">
            {`Reason: ${user?.suspensionReason}`}
          </span>
        )}
      </>
    )
  }

  const wholesellerInfoItems = [
    {
      label: 'Name',
      value: (
        <DisplayShopName
          shop={{
            name: shopName,
            isPremium: shop.isPremium,
            isVerified: shop.isApproved,
          }}
        />
      ),
    },
    { label: 'Phone Number', value: user?.phoneNumber },
    {
      label: 'Status',
      value: renderStatus(),
    },
    { label: 'User Type', value: <UserTypeDisplay userType={user?.userType} /> },
    { label: 'Created', value: formatTime(user?.createdAt) },
  ]

  const shopInfoItems = [
    { label: 'Shop Name', value: shopName },
    { label: 'Building Name', value: buildingName },
    { label: 'Floor', value: floor },
    { label: 'Shop Number', value: shopNumber },
    {
      label: 'Verification',
      value: isApproved ? 'Verified' : 'Not Verified',
      className: isApproved ? 'text-success-600' : 'text-error-700',
    },
    { label: 'Approved At', value: formatTime(approvedAt) },
    { label: 'Approved By', value: approvedBy },
    {
      label: 'Documents',
      value: hasDocuments ? `${shop.shopDocuments.length} submitted` : 'Not submitted',
      className: hasDocuments ? 'text-success-600' : 'text-error-700',
    },
    { label: 'Tickets', value: activeTickets ?? 0 },
    { label: 'Violations', value: violationReports ?? 0 },
  ]

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-full">
          <h2 className="text-lg font-semibold mb-4 text-foreground">Shop Info</h2>
          <InfoGrid infoItems={shopInfoItems} />
        </div>
        <div className="h-full">
          <h2 className="text-lg font-semibold mb-4 text-foreground flex justify-between items-center">
            Wholeseller Info
            <ShopActionButtons shop={shop} onSuspendClick={() => setIsSuspendModalOpen(true)} />
          </h2>
          <InfoGrid infoItems={wholesellerInfoItems} />
        </div>
      </div>
      <SubscriptionDetails subscription={userSubscription} />

      <div className="w-full mt-8">
        <DocumentListing
          documents={documents}
          shopId={shop?.id}
          userType={USER_TYPE.wholeseller}
          readOnly={true}
        />
      </div>

      <SuspendModal
        isOpen={isSuspendModalOpen}
        onClose={onCloseSuspendModal}
        shopSlug={shopSlug}
        user={{
          id: user?.id,
          name: shopName,
          buildingName,
          floor,
          shopNumber,
          isActive: user?.isActive,
          createdAt: shop?.createdAt,
          userType: USER_TYPE.wholeseller,
        }}
      />
    </>
  )
}
